/**
 * Google Tag Manager for https://cloud.digitalanarchy.com
 *
 * GA4 and Google Ads tags are configured inside GTM (GTM-TGN2FPH4).
 * This module loads the container on production and pushes purchase events
 * to dataLayer for GTM to pick up.
 *
 * Drop-in usage in any page component:
 *
 *   import { useGoogleAds, googleAds } from 'renderer/shared/analytics';
 *
 *   // GTM container — store home only (#/)
 *   useGoogleAds('home');
 *
 *   // GTM container — any route (product pages, cart, etc.)
 *   useGoogleAds('base');
 *
 *   // Purchase event — pass API response, or omit to read sessionStorage
 *   useGoogleAds('purchase', order);
 *   useGoogleAds('purchase');
 *
 *   // Before redirecting to /confirmation after checkout:
 *   googleAds.savePurchaseForConfirmation(result);
 */
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { PurchaseConfirmation } from 'renderer/features/purchase-page/schemas/purchase.schemas';

export const GTM_ID = 'GTM-TGN2FPH4';

/** Configured in GTM — kept for reference. */
export const GOOGLE_ANALYTICS_ID = 'G-S7SRH6J0P7';
export const GOOGLE_ADS_ID = 'AW-1063405546';
export const GOOGLE_ADS_PURCHASE_SEND_TO = 'AW-1063405546/VH-VCKe3ua8cEOqPifsD';

/** dataLayer event name — GTM trigger should listen for this. */
export const GTM_PURCHASE_EVENT = 'purchase';

const PURCHASE_CONFIRMATION_STORAGE_KEY = 'da_purchase_confirmation';
const HOME_HASHES = ['#/', '#', ''];
const CONVERSION_TRACKED_PREFIX = 'google_ads_purchase_conversion:';

let tagLoaded = false;

export type GoogleAdsScope =
  | 'home' // GTM on production store home (#/)
  | 'base' // GTM on any production cloud route
  | 'purchase'; // purchase dataLayer event (thank-you page)

export type PurchaseConversionParams = {
  transactionId: string;
  value: number;
  currency: string;
  newCustomer?: boolean;
};

function isCloudProductionSite(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const { protocol, hostname } = window.location;
  return protocol === 'https:' && hostname === 'cloud.digitalanarchy.com';
}

function shouldLoadOnHome(pathname: string): boolean {
  if (!isCloudProductionSite()) {
    return false;
  }

  return pathname === '/' && HOME_HASHES.includes(window.location.hash);
}

function injectGTM(): void {
  if (tagLoaded) {
    return;
  }

  tagLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

function ensureGTM(): void {
  if (!isCloudProductionSite()) {
    return;
  }

  injectGTM();
}

function loadGTMForHome(pathname: string): void {
  if (!shouldLoadOnHome(pathname)) {
    return;
  }

  ensureGTM();
}

function readStoredPurchaseConfirmation(): PurchaseConfirmation | null {
  try {
    const raw = sessionStorage.getItem(PURCHASE_CONFIRMATION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    sessionStorage.removeItem(PURCHASE_CONFIRMATION_STORAGE_KEY);
    return JSON.parse(raw) as PurchaseConfirmation;
  } catch {
    return null;
  }
}

function purchaseToConversionParams(
  data: PurchaseConfirmation | null | undefined,
): PurchaseConversionParams | null {
  if (!data) {
    return null;
  }

  const transactionId = data.transactionId ?? data.transactions?.[0]?._id ?? '';

  if (!transactionId) {
    return null;
  }

  const value = (data.transactions ?? []).reduce((sum, txn) => {
    const lineTotal = txn.prices?.totalPrice ?? txn.amount ?? 0;
    return sum + lineTotal;
  }, 0);

  const newCustomer =
    typeof data.newCustomer === 'boolean' ? data.newCustomer : undefined;

  return {
    transactionId,
    value,
    currency: 'USD',
    ...(newCustomer !== undefined ? { newCustomer } : {}),
  };
}

function trackPurchaseConversion(params: PurchaseConversionParams): void {
  if (!isCloudProductionSite()) {
    return;
  }

  const { transactionId, value, currency, newCustomer } = params;
  const dedupKey = `${CONVERSION_TRACKED_PREFIX}${transactionId}`;

  if (sessionStorage.getItem(dedupKey)) {
    return;
  }

  ensureGTM();

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: GTM_PURCHASE_EVENT,
    transaction_id: transactionId,
    value,
    currency,
    ...(newCustomer !== undefined ? { new_customer: newCustomer } : {}),
  });

  sessionStorage.setItem(dedupKey, '1');
}

/** Non-hook helpers (e.g. checkout redirect). */
export const googleAds = {
  /** Call after a successful sale API response, before navigating to /confirmation. */
  savePurchaseForConfirmation(purchase: PurchaseConfirmation): void {
    sessionStorage.setItem(
      PURCHASE_CONFIRMATION_STORAGE_KEY,
      JSON.stringify(purchase),
    );
  },
};

/**
 * Loads GTM and/or pushes a purchase event to dataLayer on production cloud.
 */
export function useGoogleAds(
  scope: GoogleAdsScope,
  purchase?: PurchaseConfirmation | null,
): void {
  const { pathname } = useLocation();

  const order = useMemo(() => {
    if (scope !== 'purchase') {
      return null;
    }

    return purchase ?? readStoredPurchaseConfirmation();
  }, [scope, purchase]);

  const conversionParams = useMemo(
    () => (scope === 'purchase' ? purchaseToConversionParams(order) : null),
    [scope, order],
  );

  useEffect(() => {
    if (scope === 'home') {
      loadGTMForHome(pathname);
      return;
    }

    if (scope === 'base') {
      ensureGTM();
      return;
    }

    if (scope === 'purchase' && conversionParams) {
      trackPurchaseConversion(conversionParams);
    }
  }, [scope, pathname, conversionParams]);
}

/** @internal */
export function resetGoogleAdsTagStateForTests(): void {
  tagLoaded = false;
}

/** @internal */
export function setGoogleAdsTagLoadedForTests(loaded = true): void {
  tagLoaded = loaded;
}

/** @internal — unit tests */
export const googleAdsInternals = {
  isCloudProductionSite,
  shouldLoadOnHome,
  purchaseToConversionParams,
  trackPurchaseConversion,
};
